import { formatCurrency } from '@/lib/utils'
import type { Job } from '@/types'

export const JOB_LOCKED_BADGE_CLASS =
  'gap-1 border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'

export const jobStatusColors: Record<string, string> = {
  draft: 'border-slate-200 bg-slate-50 text-slate-700',
  open: 'border-blue-200 bg-blue-50 text-blue-700',
  in_progress: 'border-amber-200 bg-amber-50 text-amber-700',
  completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  cancelled: 'border-rose-200 bg-rose-50 text-rose-700',
  on_hold: 'border-orange-200 bg-orange-50 text-orange-700',
}

export const jobStatusAccentGradients: Record<string, string> = {
  draft: 'from-slate-400 via-slate-300 to-slate-200',
  open: 'from-blue-600 via-sky-500 to-cyan-400',
  in_progress: 'from-amber-500 via-orange-400 to-yellow-400',
  completed: 'from-emerald-600 via-green-500 to-teal-400',
  cancelled: 'from-rose-600 via-red-500 to-pink-400',
  on_hold: 'from-orange-500 via-amber-400 to-yellow-300',
}

export const jobPriorityColors: Record<string, string> = {
  low: 'border-slate-200 bg-slate-50 text-slate-700',
  medium: 'border-blue-200 bg-blue-50 text-blue-700',
  high: 'border-orange-200 bg-orange-50 text-orange-700',
  urgent: 'border-rose-200 bg-rose-50 text-rose-700',
  critical: 'border-violet-200 bg-violet-50 text-violet-700',
}

export const jobPriorityLabels: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
  critical: 'Critical',
}

export const jobRecurrenceLabels: Record<string, string> = {
  none: 'No repeat',
  daily: 'Daily',
  weekly: 'Weekly',
  bi_weekly: 'Bi-weekly',
  monthly: 'Monthly',
  every_n_days: 'Every N days',
  weekly_days: 'Weekly on days',
  monthly_day: 'Monthly on day',
}

export const jobRecurrenceWeekdayShortLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export const NEW_JOB_WINDOW_MS = 2 * 60 * 60 * 1000

export function formatJobAmount(job: Job): string {
  const candidates = [
    job.job_pay,
    job.budget,
    job.order_item_info?.final_price,
  ]

  for (const value of candidates) {
    if (value === null || value === undefined || value === '') {
      continue
    }

    const parsed = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(parsed)) {
      return formatCurrency(parsed)
    }
  }

  return '-'
}

export function getJobAdvancePayment(job: Job): number {
  const candidates = [job.total_advance_payment, job.advance_payment]
  for (const value of candidates) {
    if (value === null || value === undefined) {
      continue
    }
    const parsed = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return 0
}

export function formatJobPaymentColumn(job: Job): { advanceLabel: string; dueLabel: string | null } {
  const advance = getJobAdvancePayment(job)
  const advanceLabel = advance > 0 ? formatCurrency(advance) : '-'

  if (!job.has_invoice) {
    return { advanceLabel, dueLabel: null }
  }

  const dueCandidates = [job.invoice_amount_due, job.payment_due]
  for (const value of dueCandidates) {
    if (value === null || value === undefined) {
      continue
    }
    const parsed = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(parsed)) {
      return { advanceLabel, dueLabel: formatCurrency(parsed) }
    }
  }

  return { advanceLabel, dueLabel: '-' }
}

const parseAmount = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const maxParsedAmount = (values: unknown[]): number | null =>
  values.reduce<number | null>((current, value) => {
    const parsed = parseAmount(value)
    if (parsed === null) {
      return current
    }
    return current === null ? parsed : Math.max(current, parsed)
  }, null)

export function getJobSpendSummary(job: Job): { estimated: number | null; actual: number | null } {
  const estimated = maxParsedAmount([
    job.total_estimated_spent,
    job.worker_budget_amount,
    job.budget,
  ])

  const recordedExpenses = maxParsedAmount([
    job.job_expenses,
    job.actual_cost,
    job.job_summary?.total_expenses,
  ]) ?? 0
  const workerPay = parseAmount(job.worker_pay_total) ?? 0
  const actual = maxParsedAmount([
    job.total_actual_spent,
    workerPay + recordedExpenses,
  ])

  return { estimated, actual }
}

export function getJobBudgetOverrun(job: Job): { budget: number; expenses: number; overBy: number } | null {
  const { estimated, actual } = getJobSpendSummary(job)
  if (estimated === null || estimated <= 0 || actual === null || actual <= estimated) {
    return null
  }

  return {
    budget: estimated,
    expenses: actual,
    overBy: actual - estimated,
  }
}

export function isRecurringJob(job: Job): boolean {
  if (job.recurrence_rule && job.recurrence_rule !== 'none') {
    return true
  }

  if (job.recurrence_parent_info?.id != null) {
    return true
  }

  const parentId = job.recurrence_parent
  return parentId != null && String(parentId).trim() !== ''
}

export function isRecurringSeriesSourceJob(job: Job): boolean {
  return Boolean(job.recurrence_rule && job.recurrence_rule !== 'none' && !job.recurrence_parent_info?.id && !job.recurrence_parent)
}

export function countRecurringJobs(jobs: Job[]): number {
  return jobs.filter(isRecurringJob).length
}

export function getRecurrenceLabel(job: Job): string | null {
  if (!job.recurrence_rule || job.recurrence_rule === 'none') {
    return null
  }

  if (job.recurrence_rule === 'every_n_days' && job.recurrence_interval_days) {
    return `Every ${job.recurrence_interval_days} days`
  }

  if (job.recurrence_rule === 'weekly_days' && job.recurrence_weekdays?.length) {
    const labels = job.recurrence_weekdays
      .map((day) => jobRecurrenceWeekdayShortLabels[day] || '')
      .filter(Boolean)
    if (labels.length > 0) {
      return `Weekly on ${labels.join(', ')}`
    }
  }

  if (job.recurrence_rule === 'monthly_day' && job.recurrence_month_day) {
    return `Monthly on day ${job.recurrence_month_day}`
  }

  return jobRecurrenceLabels[job.recurrence_rule] || 'Repeats'
}

export function getRecurrenceOccurrenceHint(job: Job): string | null {
  if (!isRecurringJob(job)) {
    return null
  }

  const ruleLabel = getRecurrenceLabel(job)
  const remaining = job.recurrence_count
  const endDate = job.recurrence_end ? formatJobScheduleLabel(job.recurrence_end) : null

  if (job.recurrence_parent_info?.id != null) {
    const parentLabel = job.recurrence_parent_info.title?.trim() || `Job #${job.recurrence_parent_info.id}`
    return ruleLabel
      ? `${ruleLabel} · occurrence of ${parentLabel}`
      : `Recurring occurrence of ${parentLabel}`
  }

  if (remaining != null && remaining > 0) {
    return ruleLabel
      ? `${ruleLabel} · ${remaining} occurrence${remaining === 1 ? '' : 's'} remaining`
      : `${remaining} occurrence${remaining === 1 ? '' : 's'} remaining`
  }

  if (endDate && endDate !== '-') {
    return ruleLabel ? `${ruleLabel} · until ${endDate}` : `Repeats until ${endDate}`
  }

  return ruleLabel ? `${ruleLabel} · open-ended series` : 'Recurring series'
}

export function getRecentJobLabel(createdAt?: string | null, now = Date.now()): string | null {
  if (!createdAt) {
    return null
  }

  const createdAtMs = new Date(createdAt).getTime()
  if (!Number.isFinite(createdAtMs)) {
    return null
  }

  const diffMs = Math.max(0, now - createdAtMs)
  const diffMinutes = Math.floor(diffMs / (60 * 1000))
  if (diffMinutes < 1) {
    return 'Now'
  }

  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) {
    return `${diffHours}h`
  }

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) {
    return `${diffDays}d`
  }

  return `${Math.floor(diffDays / 7)}w`
}

export function isNewJob(createdAt?: string | null, now = Date.now()): boolean {
  if (!createdAt) {
    return false
  }

  const createdAtMs = new Date(createdAt).getTime()
  if (!Number.isFinite(createdAtMs)) {
    return false
  }

  return Math.max(0, now - createdAtMs) <= NEW_JOB_WINDOW_MS
}

export function getJobClientName(job: Job): string {
  return job.client_user_info?.name
    || job.client_name
    || job.order_info?.customer_name
    || 'No client linked'
}

export function getJobWorkerInitials(name?: string | null): string {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (parts.length === 0) {
    return '?'
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }

  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

export function formatJobScheduleLabel(value?: string | null): string {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (!Number.isFinite(parsed.getTime())) {
    return '-'
  }

  return parsed.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export type JobScheduleState = 'in_progress' | 'overdue' | 'upcoming' | 'none'

const TERMINAL_JOB_STATUSES = new Set(['completed', 'cancelled'])

function parseJobDateMs(value?: string | null): number | null {
  if (!value) {
    return null
  }

  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : null
}

export function resolveJobScheduleState(job: Job, now = Date.now()): JobScheduleState {
  if (TERMINAL_JOB_STATUSES.has(job.status) || job.status === 'on_hold') {
    return 'none'
  }

  const startMs = parseJobDateMs(job.start_date ?? job.scheduled_start)
  const endMs = parseJobDateMs(job.end_date ?? job.scheduled_end)

  // Underway by date/time → Live (events no longer use a stored in_progress status).
  if (startMs != null && startMs <= now && (endMs == null || endMs >= now)) {
    return 'in_progress'
  }

  if (endMs != null && endMs < now) {
    return 'overdue'
  }

  if (startMs != null && startMs > now) {
    return 'upcoming'
  }

  if (job.status === 'in_progress') {
    return 'in_progress'
  }

  return 'none'
}

export const jobScheduleStateLabels: Record<Exclude<JobScheduleState, 'none'>, string> = {
  in_progress: 'Live',
  overdue: 'Overdue',
  upcoming: 'Upcoming',
}

/** Event list/detail badge: Live is schedule-derived; On Hold stays manual. */
export function resolveEventDisplayStatus(job: Job, now = Date.now()): { code: string; label: string } {
  const stored = String(job.status || 'draft').trim().toLowerCase()
  if (stored === 'draft') return { code: 'draft', label: 'Draft' }
  if (stored === 'on_hold') return { code: 'on_hold', label: 'On Hold' }
  if (stored === 'cancelled') return { code: 'cancelled', label: 'Cancelled' }
  if (stored === 'completed') return { code: 'completed', label: 'Completed' }
  if (resolveJobScheduleState(job, now) === 'in_progress') {
    return { code: 'in_progress', label: 'Live' }
  }
  return { code: 'scheduled', label: 'Scheduled' }
}

export const jobScheduleStateBadgeClasses: Record<Exclude<JobScheduleState, 'none'>, string> = {
  in_progress: 'border-amber-300 bg-amber-50 text-amber-800',
  overdue: 'border-orange-300 bg-orange-50 text-orange-800',
  upcoming: 'border-sky-300 bg-sky-50 text-sky-800',
}

export const jobScheduleStateCardClasses: Record<JobScheduleState, string> = {
  in_progress: 'border-2 border-amber-400/80 bg-amber-50/20 shadow-[inset_3px_0_0_0_rgb(245,158,11)]',
  overdue: 'border-2 border-orange-500/80 bg-orange-50/25 shadow-[inset_3px_0_0_0_rgb(249,115,22)]',
  upcoming: 'border-2 border-sky-400/70 bg-sky-50/15 shadow-[inset_3px_0_0_0_rgb(14,165,233)]',
  none: 'border border-border/70 bg-card/95',
}

const JOB_CATEGORY_COLOR_PALETTE = [
  { badge: 'border-violet-200 bg-violet-50 text-violet-800', accent: 'from-violet-500 to-purple-400' },
  { badge: 'border-sky-200 bg-sky-50 text-sky-800', accent: 'from-sky-500 to-cyan-400' },
  { badge: 'border-emerald-200 bg-emerald-50 text-emerald-800', accent: 'from-emerald-500 to-teal-400' },
  { badge: 'border-orange-200 bg-orange-50 text-orange-800', accent: 'from-orange-500 to-amber-400' },
  { badge: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800', accent: 'from-fuchsia-500 to-pink-400' },
  { badge: 'border-indigo-200 bg-indigo-50 text-indigo-800', accent: 'from-indigo-500 to-blue-400' },
  { badge: 'border-lime-200 bg-lime-50 text-lime-800', accent: 'from-lime-500 to-green-400' },
  { badge: 'border-rose-200 bg-rose-50 text-rose-800', accent: 'from-rose-500 to-red-400' },
] as const

function hashCategoryKey(value: string): number {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index)
    hash |= 0
  }

  return Math.abs(hash)
}

export function getJobCategoryColor(categoryKey?: string | number | null) {
  const key = String(categoryKey ?? '').trim() || 'uncategorized'
  return JOB_CATEGORY_COLOR_PALETTE[hashCategoryKey(key) % JOB_CATEGORY_COLOR_PALETTE.length]
}

export function resolveJobCategoryKey(job: Job): string {
  if (job.category != null && String(job.category).trim() !== '') {
    return String(job.category)
  }

  if (job.category_name) {
    return job.category_name
  }

  return 'uncategorized'
}

export const JOB_SORT_OPTIONS = [
  { value: 'scheduled_start', label: 'Start date (soonest)' },
  { value: '-scheduled_start', label: 'Start date (latest)' },
  { value: 'scheduled_end', label: 'End date (soonest)' },
  { value: '-scheduled_end', label: 'End date (latest)' },
  { value: '-created_at', label: 'Created (newest)' },
  { value: 'created_at', label: 'Created (oldest)' },
] as const

export type JobSortOption = typeof JOB_SORT_OPTIONS[number]['value']

export const JOB_SCHEDULE_FILTER_OPTIONS = [
  { value: 'all', label: 'All timing', badgeClassName: 'border-border bg-background text-muted-foreground', activeClassName: 'border-foreground/30 bg-muted ring-1 ring-border' },
  { value: 'upcoming', label: 'Upcoming', badgeClassName: 'border-sky-200 bg-sky-50 text-sky-700', activeClassName: 'border-sky-400 bg-sky-100 ring-1 ring-sky-300' },
  { value: 'overdue', label: 'Overdue', badgeClassName: 'border-rose-200 bg-rose-50 text-rose-700', activeClassName: 'border-rose-400 bg-rose-100 ring-1 ring-rose-300' },
  { value: 'in_progress', label: 'In progress', badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700', activeClassName: 'border-amber-400 bg-amber-100 ring-1 ring-amber-300' },
] as const

export function toJobCalendarDateKey(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function resolveJobCalendarDate(job: Job) {
  const value = job.start_date ?? job.scheduled_start
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  return Number.isFinite(parsed.getTime()) ? parsed : null
}

export function groupJobsByCalendarDate(jobs: Job[]) {
  const map = new Map<string, Job[]>()

  for (const job of jobs) {
    const calendarDate = resolveJobCalendarDate(job)
    if (!calendarDate) {
      continue
    }

    const key = toJobCalendarDateKey(calendarDate)
    const current = map.get(key) ?? []
    current.push(job)
    map.set(key, current)
  }

  for (const [key, items] of map.entries()) {
    items.sort((left, right) => {
      const leftDate = resolveJobCalendarDate(left)?.getTime() ?? 0
      const rightDate = resolveJobCalendarDate(right)?.getTime() ?? 0
      return leftDate - rightDate
    })
    map.set(key, items)
  }

  return map
}

const DAY_MINUTES = 24 * 60

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
}

function endOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
}

function resolveJobTimelineBounds(job: Job) {
  const rawStart = job.start_date ?? job.scheduled_start
  const rawEnd = job.end_date ?? job.scheduled_end

  if (!rawStart || !rawEnd) {
    return null
  }

  const startDate = new Date(rawStart)
  const endDate = new Date(rawEnd)

  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) {
    return null
  }

  if (endDate.getTime() <= startDate.getTime()) {
    return null
  }

  return {
    startDate,
    endDate,
  }
}

function jobStartsOnCalendarDay(job: Job, selectedDate: Date) {
  const calendarDate = resolveJobCalendarDate(job)
  if (!calendarDate) {
    return false
  }

  return toJobCalendarDateKey(calendarDate) === toJobCalendarDateKey(selectedDate)
}

function jobOverlapsCalendarDay(job: Job, selectedDate: Date) {
  const bounds = resolveJobTimelineBounds(job)
  if (!bounds) {
    return false
  }

  const dayStart = startOfLocalDay(selectedDate).getTime()
  const dayEnd = endOfLocalDay(selectedDate).getTime()

  return bounds.startDate.getTime() <= dayEnd && bounds.endDate.getTime() >= dayStart
}

export function filterJobsOverlappingCalendarDay(jobs: Job[], selectedDate: Date) {
  return jobs
    .filter((job) => jobOverlapsCalendarDay(job, selectedDate) || jobStartsOnCalendarDay(job, selectedDate))
    .sort((left, right) => {
      const leftDate = resolveJobCalendarDate(left)?.getTime() ?? 0
      const rightDate = resolveJobCalendarDate(right)?.getTime() ?? 0
      return leftDate - rightDate
    })
}

export type JobDayTimelineEntry = {
  job: Job
  startMinutes: number
  endMinutes: number
}

export function formatJobTimelineClock(minutes: number) {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, minutes))
  const hours = Math.floor(clamped / 60)
  const mins = Math.round(clamped % 60)
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export function buildJobDayTimelineEntries(jobs: Job[], selectedDate: Date): JobDayTimelineEntry[] {
  const dayStart = startOfLocalDay(selectedDate)
  const dayEnd = endOfLocalDay(selectedDate)
  const dayStartMs = dayStart.getTime()
  const dayEndMs = dayEnd.getTime()

  const entries: JobDayTimelineEntry[] = []

  for (const job of jobs) {
    const bounds = resolveJobTimelineBounds(job)
    if (!bounds) {
      continue
    }

    const clippedStartMs = Math.max(bounds.startDate.getTime(), dayStartMs)
    const clippedEndMs = Math.min(bounds.endDate.getTime(), dayEndMs)
    if (clippedEndMs <= clippedStartMs) {
      continue
    }

    const clippedStart = new Date(clippedStartMs)
    const clippedEnd = new Date(clippedEndMs)

    const startMinutes = clippedStart.getHours() * 60 + clippedStart.getMinutes() + clippedStart.getSeconds() / 60
    const endMinutes = clippedEnd.getHours() * 60 + clippedEnd.getMinutes() + clippedEnd.getSeconds() / 60

    if (endMinutes <= startMinutes) {
      continue
    }

    entries.push({
      job,
      startMinutes: Math.max(0, Math.min(DAY_MINUTES, startMinutes)),
      endMinutes: Math.max(0, Math.min(DAY_MINUTES, endMinutes)),
    })
  }

  return entries.sort((left, right) => {
    if (left.startMinutes !== right.startMinutes) {
      return left.startMinutes - right.startMinutes
    }
    return left.endMinutes - right.endMinutes
  })
}

export function countOverlappingJobTimelineEntries(entries: JobDayTimelineEntry[]) {
  let overlaps = 0

  for (let index = 0; index < entries.length; index += 1) {
    for (let compareIndex = index + 1; compareIndex < entries.length; compareIndex += 1) {
      const left = entries[index]
      const right = entries[compareIndex]
      if (left.startMinutes < right.endMinutes && right.startMinutes < left.endMinutes) {
        overlaps += 1
      }
    }
  }

  return overlaps
}
